import { useEffect, useRef, useState } from 'react';
import {
  ALL_EVENTS,
  MISSING,
  byNewestFirst,
  filterHistoryEvents,
  filterV1Events,
  historyEventOptions,
  historyEventQuery,
  historyOriginText,
  historyReasonText,
  historyRouteText,
  ledgerMovements,
  mergeLedgerMovements,
  shelfBalanceText,
  v1HoldsEvent,
} from '../inventoryView';
import type { HistoryFilter, HistoryRow, InventoryScreenRow } from '../inventoryView';
import type { StockHistoryAddress } from '../inventoryAddress';
import { ReservedHoldersModal } from './ReservedHoldersModal';
import type {
  FulfillmentAdminClient,
  ReservationHolder,
  StockLedgerFilters,
  StockLedgerResponse,
  StockLedgerSearchField,
} from '@/infrastructure/FulfillmentAdminClient';
import { kstTimestamp } from '@/utils/fulfillmentTime';
import { FulfillmentV2Launch } from '@/infrastructure/FulfillmentV2Launch';
import type { ProductLookupResponse, StockClient, StockEventRowResponse } from '@/infrastructure/StockClient';

/** Current inventory rows decorate a single-SKU result with catalog details when available. */
export interface StockHistoryPaneProps {
  stockClient: StockClient;
  adminClient: FulfillmentAdminClient;
  stockedRows: InventoryScreenRow[];
  onFailure: (message: string) => void;
  /**
   * The search the address carries, run on arrival and again whenever the address changes; null when it carries
   * none. Left out, the pane keeps no address and a search runs straight from the box [SSD-7444].
   */
  addressed?: StockHistoryAddress | null;
  /** Writes a new search into the address, which then comes back through `addressed` and runs. */
  onSearch?: (search: StockHistoryAddress) => void;
}

/** The five keys in the order QA-HIS-13 lists them, named as the ledger's vocabulary spells them [SSD-6456]. */
const SEARCH_FIELDS: Array<[StockLedgerSearchField, string]> = [
  ['SKU', 'SKU'],
  ['ORDER_ID', 'Order ID'],
  ['TRACKING_NO', 'Tracking No'],
  ['BARCODE', 'Barcode'],
  ['PRODUCT_NAME', 'Product Name'],
];

/**
 * QA-HIS-13's matching rules said out loud, because a partial match and an exact one need different
 * text typed in: four keys take the whole value, and only Product Name takes a fragment.
 */
const MATCH_RULE: Record<StockLedgerSearchField, string> = {
  SKU: 'Matches the whole SKU exactly.',
  ORDER_ID: 'Matches the whole order ID exactly.',
  TRACKING_NO: 'Matches the whole tracking number exactly.',
  BARCODE: 'Matches the whole barcode exactly.',
  PRODUCT_NAME: 'Matches part of the name, English or Korean, ignoring case.',
};

const PLACEHOLDER: Record<StockLedgerSearchField, string> = {
  SKU: 'Enter SKU (e.g. 100004819)',
  ORDER_ID: 'Enter the whole order ID',
  TRACKING_NO: 'Enter the tracking number on the box',
  BARCODE: 'Enter the barcode on the box',
  PRODUCT_NAME: 'Enter part of the product name (EN or KR)',
};

/**
 * One lookup as it was actually sent. `sku` is set only when SKU itself is the key, and the per-sku
 * cards hang off it: an order, tracking number, barcode or name fragment can span skus.
 */
interface ActiveSearch {
  field: StockLedgerSearchField;
  term: string;
  sku?: string;
  /** The event-type choice the ledger was asked with; ALL_EVENTS asks for every movement. */
  event: string;
}

/** One shelf of the fulfillment book, which is the same source the Current Stocks table reads. */
interface ShelfQuantity {
  locationCode: string;
  onHand: number;
  reserved: number;
}

/** The cards read the fulfillment inventory endpoint, so they can never disagree with the table [D-r 5a]. */
interface BookTotals {
  onHand: number;
  reserved: number;
  shelves: ShelfQuantity[];
}

const PAGE_SIZE = 50;

/**
 * Where a "Load more" continues: the V1 history's page token, or the V2 ledger's cursor once launched.
 * Each continues only the source that handed it out.
 */
type PageAfter = { v1Token: string } | { ledgerCursor: string };

const V2_ONLY_NOTE =
  'Fulfillment 2.0 history only: stock movements since the opening balance. Older history is migrated after launch.';

const at = (iso: string): string => kstTimestamp(iso);

// The V1 history is the load-bearing half: a ledger outage must not blank it.
const readLedger = async (
  adminClient: FulfillmentAdminClient,
  sku: string,
  event: string,
): Promise<StockLedgerResponse> => {
  try {
    return await adminClient.getStockLedger({
      searchField: 'SKU',
      keyword: sku,
      limit: PAGE_SIZE,
      ...historyEventQuery(event),
    });
  } catch {
    return { rows: [] };
  }
};

// A V1 row carries its direction in the type; a ledger row carries it in the signed delta.
const signOf = (row: HistoryRow): number => (row.delta != null ? row.delta : row.type === 'INBOUND' ? 1 : -1);

const statusClass = (status: StockEventRowResponse['status']): string => {
  if (status === 'CONFIRMED') {
    return 'stt stt-conf';
  }
  return status === 'PENDING' ? 'stt stt-pend' : 'stt stt-cancel';
};

// Only a SKU key names one sku, so only it brings the stock, shelf and product cards.
const activeSearchOf = ({ field, term }: StockHistoryAddress, event: string): ActiveSearch =>
  field === 'SKU' ? { field, term, sku: term, event } : { field, term, event };

/**
 * The lookup half of the screen: one search at a time, and every movement behind it. A key that names
 * a single sku also gets that sku's current stock, its shelves and its product card. The live screen
 * cut the event list off with a "more data available" warning — this one pages [9].
 */
export const StockHistoryPane = ({
  stockClient,
  adminClient,
  stockedRows,
  onFailure,
  addressed,
  onSearch,
}: StockHistoryPaneProps) => {
  const [field, setField] = useState<StockLedgerSearchField>('SKU');
  const [term, setTerm] = useState('');
  const [book, setBook] = useState<BookTotals | null>(null);
  const [bookUnavailable, setBookUnavailable] = useState(false);
  const [searched, setSearched] = useState<ActiveSearch | null>(null);
  const [events, setEvents] = useState<HistoryRow[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  // Once launched the ledger is the only record, so its older movements page in by cursor (SSD-7090).
  const [ledgerCursor, setLedgerCursor] = useState<string | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [product, setProduct] = useState<ProductLookupResponse | null>(null);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const [event, setEvent] = useState(ALL_EVENTS);
  // The server names its own filter vocabulary, so the event choices exist only once it has answered.
  const [ledgerFilters, setLedgerFilters] = useState<StockLedgerFilters | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  // Set once a search has read the launch switch; with Fulfillment V2 launched V1 no longer changes (SSD-7090).
  const [v2Only, setV2Only] = useState(false);
  const activeRequest = useRef(0);
  const [holdersSku, setHoldersSku] = useState<string | null>(null);
  const [holders, setHolders] = useState<ReservationHolder[]>([]);
  const [holdersLoading, setHoldersLoading] = useState(false);
  const [holdersFailed, setHoldersFailed] = useState(false);
  const holdersRequest = useRef(0);

  /** Every order holding the sku, on any shelf or none, so the Reserved figure can be traced to its orders. */
  const showHolders = async (target: string) => {
    const request = ++holdersRequest.current;
    setHoldersSku(target);
    setHolders([]);
    setHoldersFailed(false);
    setHoldersLoading(true);
    try {
      const result = await adminClient.listReservationHolders(target);
      if (request === holdersRequest.current) {
        setHolders(result.holders);
      }
    } catch {
      // A failed read is a finding, never the empty state: "nobody holds this" would be a lie.
      if (request === holdersRequest.current) {
        setHoldersFailed(true);
      }
    } finally {
      if (request === holdersRequest.current) {
        setHoldersLoading(false);
      }
    }
  };

  /** The same book the table reads, so the two totals cannot drift apart [D-r 5a]. */
  const readBook = async (target: string): Promise<BookTotals | null> => {
    try {
      const result = await adminClient.listInventory({ skus: [target], includeEmpty: true });
      return {
        onHand: result.rows.reduce((total, row) => total + row.onHand, 0),
        reserved: result.rows.reduce((total, row) => total + row.reserved, 0),
        shelves: result.rows.map((row) => ({
          locationCode: row.locationCode,
          onHand: row.onHand,
          reserved: row.reserved,
        })),
      };
    } catch {
      // The event list is the load-bearing half; a card that cannot be read simply stays away.
      return null;
    }
  };

  const load = async (target: ActiveSearch, after?: PageAfter) => {
    const token = after && 'v1Token' in after ? after.v1Token : undefined;
    const cursor = after && 'ledgerCursor' in after ? after.ledgerCursor : undefined;
    const request = after ? activeRequest.current : ++activeRequest.current;
    if (!after) {
      // A new lookup must never leave a previous search's results under the new search term.
      setBook(null);
      setBookUnavailable(false);
      setEvents([]);
      setNextToken(undefined);
      setLedgerCursor(undefined);
      setTruncated(false);
      setProduct(null);
      setFailureMessage(null);
      setSearched(target);
    }
    setLoading(true);
    try {
      const launched = await FulfillmentV2Launch.isLaunched();
      if (request !== activeRequest.current) {
        return;
      }
      setV2Only(launched);
      if (!target.sku || launched) {
        // An order, waybill, barcode or name fragment answers across skus, so only the ledger can serve it:
        // it is asked by the named field, and its failure is the whole answer's failure. Once Fulfillment V2
        // is launched the ledger is the only record for a sku too, V1 mirror rows included.
        const ledger = await adminClient.getStockLedger({
          searchField: target.field,
          keyword: target.term,
          limit: PAGE_SIZE,
          ...historyEventQuery(target.event),
          ...(cursor ? { cursor } : {}),
        });
        if (request !== activeRequest.current) {
          return;
        }
        if (ledger.filters) {
          setLedgerFilters(ledger.filters);
        }
        const rows = ledgerMovements(ledger.rows);
        // An older page holds only older movements, so it goes after the rows already shown.
        setEvents((previous) => (cursor ? [...previous, ...rows] : rows));
        // Once launched a full page hands out the cursor to the next one, so it is no longer a dead end;
        // the cut-off warning stays for a page that cannot be continued, as with the switch off.
        const next = launched ? (ledger.nextCursor ?? undefined) : undefined;
        setLedgerCursor(next);
        setTruncated(ledger.truncated === true && next === undefined);
        setSearched(target);
      } else {
        // An origin or a ledger-only type has nothing to ask V1 for.
        const result = v1HoldsEvent(target.event)
          ? await stockClient.getStockHistory({ sku: target.sku }, PAGE_SIZE, token)
          : { events: [], nextToken: undefined };
        if (request !== activeRequest.current) {
          return;
        }
        const v1Events = filterV1Events(result.events, target.event);
        // Stock-takes and moves only ever reach the V2 ledger, so the first page pulls them in beside
        // the V1 events. That ledger is kept by sku, so a search spanning skus has none to merge.
        const ledger: StockLedgerResponse = token
          ? { rows: [] }
          : await readLedger(adminClient, target.sku, target.event);
        if (request !== activeRequest.current) {
          return;
        }
        if (ledger.filters) {
          setLedgerFilters(ledger.filters);
        }
        setEvents((previous) =>
          // A later page carries older V1 rows, which still have to fall in among the ledger rows already merged.
          token ? [...previous, ...v1Events].sort(byNewestFirst) : mergeLedgerMovements(v1Events, ledger.rows),
        );
        setNextToken(result.nextToken);
        setSearched(target);
        if (!token) {
          setTruncated(ledger.truncated === true);
        }
      }
      if (!after && target.sku) {
        const bookResult = await readBook(target.sku);
        if (request === activeRequest.current) {
          setBook(bookResult);
          setBookUnavailable(bookResult === null);
        }
      }
    } catch (error) {
      if (request !== activeRequest.current) {
        return;
      }
      const message = error instanceof Error ? error.message : 'Could not read the stock history.';
      if (!after) {
        setFailureMessage(message);
      }
      onFailure(message);
      // Nothing to describe: the lookup would only decorate a result that never arrived.
      return;
    } finally {
      if (request === activeRequest.current) {
        setLoading(false);
      }
    }
    if (!after && target.sku && request === activeRequest.current) {
      try {
        const productResult = await stockClient.getProductBySku(target.sku);
        if (request === activeRequest.current) {
          setProduct(productResult);
        }
      } catch {
        // A sku the catalog does not carry still has a stock history; the product card simply stays empty.
        if (request === activeRequest.current) {
          setProduct(null);
        }
      }
    }
  };

  const search = () => {
    const typed = term.trim();
    if (!typed) {
      return;
    }
    // A new search goes through the address so it can be reloaded, shared and stepped back to; the search the
    // address already holds would not change it, so that one is simply read again.
    if (onSearch && (addressed?.field !== field || addressed?.term !== typed)) {
      onSearch({ field, term: typed });
      return;
    }
    void load(activeSearchOf({ field, term: typed }, event));
  };

  /** Shows what the address asks for: its search, run with the event type already chosen, or the empty pane. */
  const showAddressed = (address: StockHistoryAddress | null) => {
    if (!address) {
      // Back to an address with no search: whatever is still loading must not land on the emptied pane.
      activeRequest.current += 1;
      setSearched(null);
      setEvents([]);
      setBook(null);
      setBookUnavailable(false);
      setNextToken(undefined);
      setLedgerCursor(undefined);
      setTruncated(false);
      setProduct(null);
      setFailureMessage(null);
      setLoading(false);
      setTerm('');
      return;
    }
    setField(address.field);
    setTerm(address.term);
    void load(activeSearchOf(address, event));
  };
  const showAddressedRef = useRef(showAddressed);
  showAddressedRef.current = showAddressed;
  const urlDriven = onSearch !== undefined;
  const addressedField = addressed?.field;
  const addressedTerm = addressed?.term;
  // StrictMode replays this on mount; the replayed load supersedes the first before either asks for history.
  useEffect(() => {
    if (urlDriven) {
      showAddressedRef.current(addressedField && addressedTerm ? { field: addressedField, term: addressedTerm } : null);
    }
  }, [urlDriven, addressedField, addressedTerm]);

  /** The event type is asked of the server, so a new choice reads the current search again. */
  const chooseEvent = (value: string) => {
    setEvent(value);
    if (searched) {
      void load({ ...searched, event: value });
    }
  };
  const eventOptions = historyEventOptions(ledgerFilters);
  const shown = filterHistoryEvents(events, filter);
  // A search that can answer across skus has to name the sku and the product on every row [SSD-6456].
  const spansSkus = searched != null && searched.sku === undefined;
  const hasBook = book !== null && book.shelves.length > 0;
  // An emptied shelf only hides the ones that hold the sku [SSD-7445]. A negative count, or an empty shelf that still
  // holds a reservation, is a book defect and stays in sight.
  const heldShelves = book ? book.shelves.filter((shelf) => shelf.onHand !== 0 || shelf.reserved > 0) : [];
  const searchedSku = searched?.sku;
  const stockedProduct = searchedSku ? stockedRows.find((row) => row.sku === searchedSku) : undefined;
  const nameKor = stockedProduct?.nameKor || product?.nameKor;
  const barcode = stockedProduct?.barcode || product?.barcode;

  return (
    <div>
      <div className="searchbar">
        <select
          className="sel"
          aria-label="Stock history search field"
          value={field}
          onChange={(event) => setField(event.target.value as StockLedgerSearchField)}
        >
          {SEARCH_FIELDS.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <input
          className="inp"
          aria-label="Stock history search"
          placeholder={PLACEHOLDER[field]}
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && !loading && search()}
        />
        <button type="button" className="btn btn-blue btn-sm" disabled={!term.trim() || loading} onClick={search}>
          Search
        </button>
        <span className="blocked-note">{MATCH_RULE[field]}</span>
      </div>
      {v2Only && <div className="blocked-note">{V2_ONLY_NOTE}</div>}

      {searchedSku && (
        <div className="cards">
          <div className="card">
            <h4>Stock Status</h4>
            {hasBook ? (
              <div className="bigrow">
                <div className="bignum">
                  <div className="n">{book.onHand}</div>
                  <div className="l">Total Qty</div>
                </div>
                <div className="bignum">
                  <div className="n res">
                    {book.reserved > 0 ? (
                      <button
                        type="button"
                        className="reslink"
                        aria-label={`Reserved ${book.reserved}`}
                        onClick={() => void showHolders(searchedSku)}
                      >
                        {book.reserved}
                      </button>
                    ) : (
                      book.reserved
                    )}
                  </div>
                  <div className="l">Reserved Qty</div>
                </div>
                <div className="bignum">
                  <div className={`n ${book.onHand < book.reserved ? 'text-danger' : 'av'}`}>
                    {book.onHand - book.reserved}
                  </div>
                  <div className="l">Available Qty</div>
                </div>
              </div>
            ) : (
              <div className="blocked-note">
                {loading
                  ? 'Loading stock status…'
                  : (failureMessage ??
                    (bookUnavailable ? 'Stock status is unavailable.' : 'No stock status found for this SKU.'))}
              </div>
            )}
          </div>
          <div className="card">
            <h4>By Location</h4>
            {hasBook ? (
              <>
                {heldShelves.map((shelf) => (
                  <div className="loc-row" key={shelf.locationCode}>
                    <span className="loc-pill">{shelf.locationCode}</span>
                    <span className="num">
                      {shelf.onHand}
                      {shelf.onHand === 0 && shelf.reserved > 0 && (
                        <span className="loc-reserved"> · {shelf.reserved} reserved</span>
                      )}
                    </span>
                  </div>
                ))}
                {heldShelves.length === 0 && <div className="blocked-note">No location holds this sku.</div>}
              </>
            ) : (
              <div className="blocked-note">
                {loading
                  ? 'Loading locations…'
                  : (failureMessage ??
                    (bookUnavailable ? 'Locations are unavailable.' : 'No locations found for this SKU.'))}
              </div>
            )}
          </div>
          <div className="card pinfo">
            <h4>Product Information</h4>
            <div className="kv">
              <span className="k">SKU</span>
              <span>{searchedSku}</span>
            </div>
            <div className="kv">
              <span className="k">Name</span>
              <span>{product?.name ?? MISSING}</span>
            </div>
            <div className="kv">
              <span className="k">Name KR</span>
              <span>{nameKor ?? MISSING}</span>
            </div>
            <div className="kv">
              <span className="k">Brand</span>
              <span>{product?.brand ?? MISSING}</span>
            </div>
            <div className="kv">
              <span className="k">Barcode</span>
              <span>{barcode ?? MISSING}</span>
            </div>
          </div>
        </div>
      )}

      {searched && (
        <>
          <div className="evhead">
            <h4>Stock History Events</h4>
            <span className="sp" />
            {eventOptions.length > 0 && (
              <select
                className="sel"
                aria-label="Event type"
                value={event}
                disabled={loading}
                onChange={(change) => chooseEvent(change.target.value)}
              >
                <option value={ALL_EVENTS}>All Events</option>
                {eventOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            {(
              [
                ['all', 'All'],
                ['confirmed', 'Confirmed'],
                ['pending', 'Pending confirm'],
              ] as Array<[HistoryFilter, string]>
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`filterchip${filter === key ? ' on' : ''}`}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="table-wrap capped">
            <table className={spansSkus ? 'tbl ev-tbl ev-wide' : 'tbl ev-tbl'}>
              {/* Fixed columns: a long name or reason wraps between words and never widens its column (ultimate design). */}
              <colgroup>
                {spansSkus && (
                  <>
                    <col className="ev-sku" />
                    <col className="ev-product" />
                  </>
                )}
                <col className="ev-type" />
                <col className="ev-qty" />
                <col className="ev-shelf" />
                <col className="ev-reason" />
                <col className="ev-route" />
                <col className="ev-status" />
                <col className="ev-tracking" />
                <col className="ev-carrier" />
                <col className="ev-location" />
                <col className="ev-order" />
                <col className="ev-created" />
                <col className="ev-auditor" />
              </colgroup>
              <thead>
                <tr>
                  {spansSkus && (
                    <>
                      <th>SKU</th>
                      <th>Product</th>
                    </>
                  )}
                  <th>Type</th>
                  <th>Quantity</th>
                  <th>Shelf Qty</th>
                  <th>Reason</th>
                  <th>Sourcing Route</th>
                  <th>Status</th>
                  <th>Tracking No</th>
                  <th>Carrier</th>
                  <th>Location</th>
                  <th>Order ID</th>
                  <th>Created At</th>
                  <th>Auditor</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row, index) => (
                  // Two ledger rows can share type, ref and instant, more so once older pages are appended,
                  // so the position keeps every row's key unique.
                  <tr key={`${row.movementId}#${index}`} className={row.status === 'PENDING' ? 'pending' : undefined}>
                    {spansSkus && (
                      <>
                        <td className="num">{row.sku ?? MISSING}</td>
                        <td>
                          <div>{row.productName ?? MISSING}</div>
                          <div className="blocked-note">{row.productNameKr ?? MISSING}</div>
                        </td>
                      </>
                    )}
                    <td>
                      <span className={`ty ${signOf(row) > 0 ? 'ty-in' : 'ty-out'}`}>{row.type}</span>
                      {historyOriginText(row) && <div className="blocked-note">{historyOriginText(row)}</div>}
                    </td>
                    <td className="num">{signOf(row) > 0 ? `+${row.qty}` : `−${row.qty}`}</td>
                    <td className="num">{shelfBalanceText(row)}</td>
                    <td>{historyReasonText(row)}</td>
                    <td>{historyRouteText(row)}</td>
                    <td>
                      <span className={statusClass(row.status)}>{row.status}</span>
                    </td>
                    <td className="num">{row.trackingNumber ?? MISSING}</td>
                    <td>{row.carrier ?? MISSING}</td>
                    <td>
                      <span className="loc-pill">{row.locationId ?? MISSING}</span>
                    </td>
                    <td className="num">{row.orderId ?? MISSING}</td>
                    <td className="num ev-when">{at(row.createdAt)}</td>
                    <td>{row.auditor}</td>
                  </tr>
                ))}
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={spansSkus ? 14 : 12} className="blocked-note">
                      {loading ? 'Loading the movements…' : (failureMessage ?? 'No movement matches this filter.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {truncated && (
            <div role="alert" className="note warn">
              Too many ledger movements match; only the first {PAGE_SIZE} of them are shown. Narrow the search.
            </div>
          )}
          {nextToken && (
            <button
              type="button"
              className="btn btn-line btn-sm"
              style={{ marginTop: 10 }}
              disabled={loading}
              onClick={() => searched && void load(searched, { v1Token: nextToken })}
            >
              Load more
            </button>
          )}
          {ledgerCursor && (
            <button
              type="button"
              className="btn btn-line btn-sm"
              style={{ marginTop: 10 }}
              disabled={loading}
              onClick={() => searched && void load(searched, { ledgerCursor })}
            >
              Load more
            </button>
          )}
        </>
      )}

      {holdersSku && (
        <ReservedHoldersModal
          sku={holdersSku}
          holders={holders}
          loading={holdersLoading}
          failed={holdersFailed}
          onRetry={() => void showHolders(holdersSku)}
          onClose={() => {
            holdersRequest.current += 1;
            setHoldersSku(null);
          }}
        />
      )}
    </div>
  );
};
